import React, { ButtonHTMLAttributes } from 'react';

export type ButtonColorTheme = {
  primary: {
    base: 'bg-primary text-white';
    hover: 'hover:bg-secondary';
    active: 'active:bg-opacity-70';
  };
  secondary: {
    base: 'bg-secondary text-white';
    hover: 'hover:bg-primary';
    active: 'active:bg-opacity-70';
  };
  error: {
    base: 'bg-red-700 text-white';
    hover: 'hover:bg-red-600';
    active: 'active:bg-red-400';
  };
  grayScale: {
    base: 'bg-gray-500 text-white';
    hover: 'hover:bg-gray-600';
    active: 'active:bg-gray-400';
  };
  black: {
    base: 'border border-text';
    hover: 'hover:bg-text hover:text-white';
    active: 'active:text-white active:bg-opacity-70';
  };
  outline: {
    base: 'border border-text';
    hover: 'hover:bg-gray-50';
    active: 'active:text-primary active:border-primary';
  };
};

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  colorTheme?: keyof ButtonColorTheme;
  size?: 'xs' | 'small' | 'medium' | 'large';
};

const colorThemes: Record<
  keyof ButtonColorTheme,
  ButtonColorTheme[keyof ButtonColorTheme]
> = {
  primary: {
    base: 'bg-primary text-white',
    hover: 'hover:bg-secondary',
    active: 'active:bg-opacity-70',
  },
  secondary: {
    base: 'bg-secondary text-white',
    hover: 'hover:bg-primary',
    active: 'active:bg-opacity-70',
  },
  error: {
    base: 'bg-red-700 text-white',
    hover: 'hover:bg-red-600',
    active: 'active:bg-red-400',
  },
  grayScale: {
    base: 'bg-gray-500 text-white',
    hover: 'hover:bg-gray-600',
    active: 'active:bg-gray-400',
  },
  black: {
    base: 'border border-text',
    hover: 'hover:bg-text hover:text-white',
    active: 'active:text-white active:bg-opacity-70',
  },
  outline: {
    base: 'border border-text',
    hover: 'hover:bg-gray-50',
    active: 'active:text-primary active:border-primary',
  },
};

function Button({
  colorTheme = 'primary',
  size = 'medium',
  className = '',
  ...rest
}: Props) {
  const theme = colorThemes[colorTheme];

  const padding = {
    xs: 'p-1',
    small: 'p-2',
    medium: 'p-3',
    large: 'p-4',
  }[size];

  const classes = `
    ${theme.base} 
    ${theme.hover} 
    ${theme.active} 
    ${rest.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
    hover:shadow-md 
    rounded-lg
    w-full 
    font-display
    text-sm 
    sm:text-base
    md:text-lg    
    flex 
    items-center 
    justify-center 
    space-x-2
    transition-all 
    whitespace-nowrap
    ${padding} 
    ${className}
  `.trim();

  return <button className={classes} {...rest} />;
}

export default Button;
